import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Text from '@/components/Typography/Text';
import FlowField from '@/components/Landing/FlowField';
import LandingClosing from '@/components/Landing/LandingClosing';
import Reveal from '@/components/Landing/Reveal';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import {
  Accent,
  BleedRule,
  Chip,
  CtaRow,
  DisplayHeading,
  GhostCta,
  Kicker,
  Lede,
  Marquee,
  PrimaryCta,
  Section,
  Split,
  useEntrance,
  useLanding,
} from '@/components/Landing/LandingKit';
import {
  ACCENT,
  LAYOUT,
  fluid,
  isWeb,
  labelType,
} from '@/components/Landing/landingTokens';
import { isDarkTheme } from '@/constants/Colors';
import { COMPETITOR_ALTERNATIVES } from '@/constants/competitorAlternatives';
import { GAB_AI_SOFTWARE_ID } from '@/constants/config';
import { useTheme } from '@/context/ThemeContext';

const SITE = 'https://gab.ai';

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

/** One reason-to-switch card. */
function ReasonCard({ reason }) {
  const { c, isXS, isCompact, display } = useLanding();
  const h3 = display({ min: 22, max: 29, leading: 1.14, tracking: -0.03 });

  return (
    <View
      style={{
        flexGrow: 1,
        flexBasis: isCompact ? '100%' : '46%',
        minWidth: 0,
        borderRadius: 22,
        borderWidth: 1,
        borderColor: c.rule,
        backgroundColor: c.cardSolid,
        padding: isXS ? 22 : 28,
        gap: isXS ? 20 : 24,
        justifyContent: 'space-between',
      }}
    >
      <View style={{ gap: 12 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: 11,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: c.accentSoft,
            }}
          >
            <Icon name={reason.icon} size={14} color={ACCENT} solid />
          </View>
          <Text weight="bold" style={{ color: c.faint, ...labelType(9.5, 0.2) }}>
            {reason.kicker}
          </Text>
        </View>

        <DisplayHeading tag="h3" mask={false} type={h3} lines={[[reason.title]]} />

        <Text style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 440 }}>
          {reason.copy}
        </Text>
      </View>

      <View style={{ gap: 16 }}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
          {reason.chips.map((chip) => (
            <Chip key={chip}>{chip}</Chip>
          ))}
        </View>
        <GhostCta label={reason.cta} href={reason.href} style={{ height: 34 }} />
      </View>
    </View>
  );
}

/** Column headings for the comparison, hidden once the table stacks. */
function CompareHeader({ otherLabel }) {
  const { band, isCompact } = useLanding();
  if (isCompact) return null;

  return (
    <View style={{ flexDirection: 'row', gap: 20, paddingBottom: 12 }}>
      <View style={{ flex: 1.1, minWidth: 0 }} />
      <View style={{ flex: 1.3, minWidth: 0 }}>
        <Text weight="bold" style={{ color: ACCENT, ...labelType(9.5, 0.2) }}>Gab AI</Text>
      </View>
      <View style={{ flex: 1.3, minWidth: 0 }}>
        <Text weight="bold" style={{ color: band.faint, ...labelType(9.5, 0.2) }}>
          {otherLabel}
        </Text>
      </View>
    </View>
  );
}

/**
 * One comparison row.
 *
 * On compact widths the three cells stack, and the two answers grow their own
 * inline labels so the column headings above are not load-bearing.
 */
function CompareRow({ row, isFirst }) {
  const { band, isCompact, width } = useLanding();
  const pad = fluid(width, 14, 18);

  const label = (
    <Text weight="bold" style={{ color: band.ink, fontSize: 14.5, letterSpacing: -0.15, lineHeight: 21 }}>
      {row.label}
    </Text>
  );

  const gab = (
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 9 }}>
      <View style={{ paddingTop: 5 }}>
        <Icon name="check" size={10} color={ACCENT} solid />
      </View>
      <Text style={{ color: band.inkSoft, flex: 1, minWidth: 0, fontSize: 13.5, lineHeight: 21 }}>
        {row.gab}
      </Text>
    </View>
  );

  const other = (
    <Text style={{ color: band.faint, fontSize: 13.5, lineHeight: 21 }}>
      {row.other}
    </Text>
  );

  if (isCompact) {
    return (
      <View
        style={{
          gap: 10,
          paddingVertical: pad + 4,
          borderTopWidth: isFirst ? 0 : 1,
          borderTopColor: band.rule,
        }}
      >
        {label}
        {gab}
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 9 }}>
          <View style={{ paddingTop: 5 }}>
            <Icon name="xmark" size={10} color={band.faint} solid />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>{other}</View>
        </View>
      </View>
    );
  }

  return (
    <View
      style={{
        flexDirection: 'row',
        gap: 20,
        alignItems: 'flex-start',
        paddingVertical: pad,
        borderTopWidth: isFirst ? 0 : 1,
        borderTopColor: band.rule,
      }}
    >
      <View style={{ flex: 1.1, minWidth: 0 }}>{label}</View>
      <View style={{ flex: 1.3, minWidth: 0 }}>{gab}</View>
      <View style={{ flex: 1.3, minWidth: 0 }}>{other}</View>
    </View>
  );
}

/** One numbered migration step. */
function StepRow({ step, index, isLast }) {
  const { c, width } = useLanding();

  return (
    <View
      style={{
        flexDirection: 'row',
        gap: 16,
        paddingVertical: fluid(width, 18, 24),
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: c.rule,
      }}
    >
      <View
        style={{
          width: 34,
          height: 34,
          borderRadius: 11,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: c.accentSoft,
        }}
      >
        <Icon name={step.icon} size={13} color={ACCENT} solid />
      </View>
      <View style={{ flex: 1, minWidth: 0, gap: 5 }}>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 9 }}>
          <Text
            weight="bold"
            style={{ color: c.faint, fontSize: 11, fontVariant: ['tabular-nums'], letterSpacing: 0.2 }}
          >
            {String(index + 1).padStart(2, '0')}
          </Text>
          <Text weight="bold" style={{ color: c.ink, flex: 1, minWidth: 0, fontSize: 15.5, letterSpacing: -0.15 }}>
            {step.title}
          </Text>
        </View>
        <Text style={{ color: c.muted, fontSize: 13.5, lineHeight: 21 }}>{step.copy}</Text>
      </View>
    </View>
  );
}

/** Sibling alternative pages for internal SEO links. */
function SiblingLinks({ currentSlug }) {
  const { c, isXS, width } = useLanding();
  const siblings = COMPETITOR_ALTERNATIVES.filter((page) => page.slug !== currentSlug);

  return (
    <View style={{ marginTop: fluid(width, 28, 40), gap: 14 }}>
      <Text weight="bold" style={{ color: c.faint, ...labelType(9.5, 0.2) }}>
        Also compare
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: isXS ? 8 : 10 }}>
        {siblings.map((page) => (
          <GhostCta
            key={page.slug}
            label={`${page.competitor} alternative`}
            href={page.slug}
            style={{ height: 34 }}
          />
        ))}
      </View>
    </View>
  );
}

/* ------------------------------------------------------------------ */

export default function CompetitorAlternativePage({
  title,
  description,
  canonical,
  keywords,
  page,
}) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();
  const { c, band, isXS, isCompact, width, pad, display } = useLanding();
  const entered = useEntrance();

  useLandingStyles();

  const headline = display({ min: 38, max: 74, leading: 0.98, tracking: -0.042 });
  const h2 = display({ min: 30, max: 52, leading: 1.02, tracking: -0.038 });

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE}${page.slug}#page`,
        url: `${SITE}${page.slug}`,
        name: page.breadcrumbLabel,
        description,
        isPartOf: { '@id': GAB_AI_SOFTWARE_ID },
        breadcrumb: { '@id': `${SITE}${page.slug}#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE}${page.slug}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Gab AI', item: SITE },
          { '@type': 'ListItem', position: 2, name: page.breadcrumbLabel, item: `${SITE}${page.slug}` },
        ],
      },
      {
        '@type': 'ItemList',
        '@id': `${SITE}${page.slug}#reasons`,
        name: `Reasons to switch from ${page.competitor}`,
        itemListElement: page.reasons.map((reason, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: reason.title,
          description: reason.copy,
        })),
      },
      {
        '@type': 'HowTo',
        '@id': `${SITE}${page.slug}#import`,
        name: page.howtoName,
        step: page.steps.map((step, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: step.title,
          text: step.copy,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE}${page.slug}#faq`,
        mainEntity: page.faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  }), [page, description]);

  const { headline: h } = page;

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title={title}
        description={description}
        canonical={canonical}
        keywords={keywords}
        structuredData={structuredData}
      />

      <ScrollView
        {...(isWeb ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* ============ MASTHEAD ============ */}
        <View
          accessibilityRole={isWeb ? 'region' : undefined}
          accessibilityLabel={page.mastheadLabel}
          style={{
            width: '100%',
            maxWidth: LAYOUT.max,
            paddingHorizontal: pad,
            paddingTop: (isXS ? insets.top : 0) + fluid(width, 104, 152),
            paddingBottom: fluid(width, 40, 60),
          }}
        >
          <View
            style={isWeb ? {
              opacity: entered ? 1 : 0,
              transition: 'opacity 700ms ease 120ms',
            } : null}
          >
            <Kicker>The alternative</Kicker>
          </View>

          <DisplayHeading
            tag="h1"
            entered={entered}
            type={headline}
            style={{ marginTop: fluid(width, 22, 32) }}
            lines={[
              [h.line1],
              [
                h.beforeAccent,
                <Accent key="a" size={headline.fontSize} leading={headline.lineHeight}>{h.accent}</Accent>,
                h.afterAccent,
              ],
            ]}
          />

          <View style={{ height: 1, backgroundColor: c.rule, marginTop: fluid(width, 30, 48) }} />

          <View
            style={{
              flexDirection: isCompact ? 'column' : 'row',
              gap: isCompact ? 30 : 56,
              alignItems: isCompact ? 'stretch' : 'flex-start',
              paddingTop: fluid(width, 26, 40),
              ...(isWeb ? {
                opacity: entered ? 1 : 0,
                transform: [{ translateY: entered ? 0 : 18 }],
                transition: 'opacity 800ms ease 420ms, transform 800ms ease 420ms',
              } : {}),
            }}
          >
            <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0 }}>
              <Lede max={480}>{page.lede}</Lede>
            </View>

            <View style={{ flex: isCompact ? undefined : 1, minWidth: 0, gap: 18 }}>
              <CtaRow>
                <PrimaryCta label="Start free" full={isXS} />
                <GhostCta label={page.importCta} href={page.importHref || '/switch'} />
              </CtaRow>
              <Text style={{ color: c.faint, fontSize: 12.5, letterSpacing: 0.1 }}>
                {page.creditNote}
              </Text>
            </View>
          </View>
        </View>

        <BleedRule />
        <Marquee items={page.marquee} />
        <BleedRule />

        {/* ============ WHY SWITCH ============ */}
        <Section label={page.whySwitchLabel}>
          <Reveal>
            <Kicker>{page.whyKicker}</Kicker>
            <DisplayHeading
              tag="h2"
              mask={false}
              type={h2}
              style={{ marginTop: 22 }}
              lines={page.whyHeading}
            />
            <Lede max={540} style={{ marginTop: 20 }}>
              {page.whyLede}
            </Lede>
          </Reveal>

          <Reveal delay={100}>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: isXS ? 12 : 16,
                marginTop: fluid(width, 36, 56),
              }}
            >
              {page.reasons.map((reason) => (
                <ReasonCard key={reason.key} reason={reason} />
              ))}
            </View>
          </Reveal>
        </Section>

        {/* ============ COMPARISON (band) ============ */}
        <View
          style={{
            width: '100%',
            alignSelf: 'stretch',
            alignItems: 'center',
            backgroundColor: band.bg,
            overflow: 'hidden',
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderColor: band.rule,
          }}
        >
          {isWeb ? (
            <View
              aria-hidden
              pointerEvents="none"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(ellipse 48% 66% at 86% 26%, ${band.glow}, transparent 68%)`,
              }}
            />
          ) : null}

          <Section label={page.compareSectionLabel} background="transparent">
            <Reveal>
              <Kicker tone="band">{page.compareKicker}</Kicker>
              <DisplayHeading
                tag="h2"
                mask={false}
                type={h2}
                color={band.ink}
                style={{ marginTop: 22 }}
                lines={page.compareHeading}
              />
              <Lede tone="band" max={560} style={{ marginTop: 20 }}>
                {page.compareLede}
              </Lede>
            </Reveal>

            <Reveal delay={100}>
              <View style={{ marginTop: fluid(width, 34, 50) }}>
                <CompareHeader otherLabel={page.compareOtherLabel} />
                <View style={{ borderTopWidth: 1, borderTopColor: band.rule }}>
                  {page.comparison.map((row, index) => (
                    <CompareRow key={row.label} row={row} isFirst={index === 0} />
                  ))}
                </View>
              </View>
            </Reveal>
          </Section>
        </View>

        {/* ============ MIGRATION ============ */}
        <Section label={page.migrateSectionLabel}>
          <Split ratio={[1, 1.25]} align="flex-start">
            <Reveal>
              <Kicker>{page.migrateKicker}</Kicker>
              <DisplayHeading
                tag="h2"
                mask={false}
                type={h2}
                style={{ marginTop: 22 }}
                lines={page.migrateHeading}
              />
              <Lede max={400} style={{ marginTop: 20 }}>
                {page.migrateLede}
              </Lede>
              <GhostCta label={page.migrateCta} href={page.migrateHref || '/switch'} style={{ marginTop: 10 }} />
            </Reveal>

            <Reveal delay={120}>
              <View
                style={{
                  borderRadius: 18,
                  borderWidth: 1,
                  borderColor: c.rule,
                  backgroundColor: c.cardSolid,
                  paddingHorizontal: isXS ? 18 : 24,
                  paddingVertical: 6,
                }}
              >
                {page.steps.map((step, index) => (
                  <StepRow
                    key={step.title}
                    step={step}
                    index={index}
                    isLast={index === page.steps.length - 1}
                  />
                ))}
              </View>
            </Reveal>
          </Split>
        </Section>

        <BleedRule />

        {/* ============ FAQ ============ */}
        <Section label={page.faqSectionLabel}>
          <Split ratio={[1, 1.5]} align="flex-start">
            <Reveal>
              <Kicker>{page.faqKicker}</Kicker>
              <DisplayHeading
                tag="h2"
                mask={false}
                type={h2}
                style={{ marginTop: 22 }}
                lines={page.faqHeading}
              />
              <Lede size="sm" max={340} style={{ marginTop: 20 }}>
                {page.faqLede}
              </Lede>
              {page.docsCta ? (
                <GhostCta label={page.docsCta} href={page.docsHref || '/docs/api'} style={{ marginTop: 12 }} />
              ) : null}
              <GhostCta label="Compare plans" href="/pricing" style={{ marginTop: page.docsCta ? 8 : 12 }} />
              <SiblingLinks currentSlug={page.slug} />
            </Reveal>

            <Reveal delay={100}>
              <View style={{ borderTopWidth: 1, borderTopColor: c.rule }}>
                {page.faqs.map((item) => (
                  <View
                    key={item.q}
                    style={{
                      paddingVertical: fluid(width, 18, 24),
                      borderBottomWidth: 1,
                      borderBottomColor: c.rule,
                      gap: 8,
                    }}
                  >
                    <Text
                      tag="h3"
                      weight="bold"
                      style={{ color: c.ink, fontSize: 16.5, letterSpacing: -0.2, lineHeight: 24 }}
                    >
                      {item.q}
                    </Text>
                    <Text tag="p" style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 620 }}>
                      {item.a}
                    </Text>
                  </View>
                ))}
              </View>
            </Reveal>
          </Split>
        </Section>

        <LandingClosing />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
